/* ============================================================
   MINIMA — fluid interaction layer (2026-08-14)

   The parts of the apple-design skill that need real physics rather than CSS:
   §3 interruptibility · §4 springs · §5 velocity handoff · §6 momentum
   projection · §9 rubber-banding · §13 haptics · §14 reduced motion.

   Loaded AFTER minima.js. It is deliberately ADDITIVE — it never rebinds any
   handler minima.js owns. It only takes over a drawer panel's transform while a
   finger is actually on it, and hands control back to CSS the moment it rests.
   ============================================================ */
(function () {
  'use strict';

  var reduced =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var $ = function (sel, ctx) { return (ctx || document).querySelector(sel); };

  /* ------------------------------------------------------------------
     §4 Spring — Apple's two designer parameters, not mass/stiffness/damping.

       response — seconds for the value to reach the target. NOT a duration;
                  a spring has no fixed duration, its settle time emerges.
       damping  — 1.0 is critically damped (graceful, no overshoot).
                  ~0.8 overshoots, and is only correct when the gesture that
                  preceded it carried momentum (a flick, a throw).

     It integrates from the CURRENT value every frame, which is the whole point:
     re-targeting mid-flight keeps both position and velocity, so a grab-and-
     reverse has no discontinuity (§3, "no brick wall").
     ------------------------------------------------------------------ */
  function spring(opts) {
    var response = opts.response || 0.4;
    var damping = opts.damping == null ? 1 : opts.damping;
    var k = Math.pow((2 * Math.PI) / response, 2);
    var c = (4 * Math.PI * damping) / response;

    var x = opts.from;
    var v = opts.velocity || 0;
    var target = opts.to;
    var raf = null;
    var last = null;

    function step(now) {
      if (last === null) last = now;
      // Clamp dt: a backgrounded tab hands back a huge delta and the sheet
      // would teleport (and overshoot wildly) on the first frame.
      var dt = Math.min((now - last) / 1000, 1 / 30);
      last = now;
      v += (-k * (x - target) - c * v) * dt;
      x += v * dt;
      opts.onUpdate(x, v);
      if (Math.abs(x - target) < 0.1 && Math.abs(v) < 0.5) {
        x = target;
        v = 0;
        opts.onUpdate(x, 0);
        raf = null;
        if (opts.onRest) opts.onRest();
        return;
      }
      raf = requestAnimationFrame(step);
    }
    raf = requestAnimationFrame(step);

    return {
      retarget: function (next) {
        target = next;
        if (!raf) { last = null; raf = requestAnimationFrame(step); }
      },
      stop: function () {
        if (raf) cancelAnimationFrame(raf);
        raf = null;
        return { x: x, v: v };
      },
      isRunning: function () { return !!raf; }
    };
  }

  /* §6 Momentum projection — where a flick actually comes to rest.
     This is the exponential-decay form Apple ships, NOT the textbook
     v^2/(2*decel). d = 0.998 reads like normal scroll deceleration. */
  function project(v, d) {
    d = d || 0.998;
    return (v / 1000) * d / (1 - d);
  }

  /* §9 Rubber band — the further past the bound, the less it follows.
     Real things slow before they stop; a hard clamp reads as "frozen". */
  function rubber(over, dim, c) {
    c = c || 0.55;
    return (over * dim * c) / (dim + c * Math.abs(over));
  }

  /* §13 Haptics — Utility rule: only meaningful moments (a commit, a snap).
     Over-feedback trains people to ignore all of it. */
  function tap(ms) {
    if (reduced) return;
    try { if (navigator.vibrate) navigator.vibrate(ms || 8); } catch (e) {}
  }

  /* ------------------------------------------------------------------
     Drag-to-dismiss for a sheet that travels along X.

     `sign` is the physical direction the sheet exits toward: +1 for a panel
     anchored to the right edge (the cart drawer under RTL), -1 for one anchored
     to the left (the mobile nav). Enter and exit share that path (§7).
     ------------------------------------------------------------------ */
  function draggableSheet(root, panel, scrim, sign) {
    if (!root || !panel) return;

    var anim = null;      // the live spring, if one is running
    var dragging = false;
    var pending = null;   // gesture not yet claimed (could be a vertical scroll)
    var startX = 0;
    var baseX = 0;        // panel offset when the grab began
    var x = 0;            // current offset in px
    var vx = 0;
    var lastX = 0;
    var lastT = 0;
    var w = 1;            // measured once per gesture, never inside pointermove

    // offsetWidth forces a synchronous layout. Reading it every pointermove is
    // exactly the thrash this pass was meant to remove (§11), so it is measured
    // on pointerdown and on resize instead.
    function measure() { w = panel.offsetWidth || 1; }
    window.addEventListener('resize', measure);

    function paint(px) {
      x = px;
      panel.style.transform = 'translateX(' + px + 'px)';
      if (scrim) {
        // Continuous feedback: the dim tracks the drag the whole way (§1),
        // it does not wait for the gesture to finish.
        scrim.style.opacity = String(Math.max(0, 1 - Math.abs(px) / w));
      }
    }

    function release() {
      panel.style.transform = '';
      if (scrim) scrim.style.opacity = '';
      // classList.remove() calls setAttribute even when the class is absent, and
      // setAttribute queues a MutationObserver record even when the value does
      // not change. Calling this unconditionally from the observer below is an
      // infinite loop that hard-freezes the page. Only touch it if it is set.
      if (root.classList.contains('is-dragging')) root.classList.remove('is-dragging');
    }

    function close() {
      // Mirrors minima.js's Cart.close()/mobile-nav close. Duplicated rather
      // than imported because minima.js is a closed IIFE and exposes nothing.
      root.classList.remove('is-open');
      document.body.style.overflow = '';
      release();
    }

    function settle(toX, velocity, dismissing) {
      if (anim) anim.stop();
      anim = spring({
        from: x,
        to: toX,
        velocity: velocity,
        // §4: bounce ONLY when the gesture carried momentum. Snapping back to
        // an open sheet is a correction, so it is critically damped; a thrown
        // sheet on its way out keeps a touch of overshoot.
        damping: dismissing ? 1.0 : 0.9,
        response: 0.32,
        onUpdate: function (val) { paint(val); },
        onRest: function () {
          anim = null;
          if (dismissing) close();
          else release();
        }
      });
    }

    function onDown(e) {
      measure();
      // §3 Interruptibility: grabbing a sheet in flight must take it over from
      // its PRESENTATION value, never from the logical target, or it jumps.
      if (anim) {
        var s = anim.stop();
        anim = null;
        baseX = s.x;
        dragging = true;
        root.classList.add('is-dragging');
        // Grabbing a moving sheet IS a drag from frame one, so capture is safe
        // and wanted here: there is no click to preserve on a sheet in flight.
        try { panel.setPointerCapture(e.pointerId); } catch (err) {}
      } else {
        if (!root.classList.contains('is-open')) return;
        baseX = 0;
        pending = { x: e.clientX, y: e.clientY };
      }
      startX = e.clientX;
      lastX = e.clientX;
      lastT = e.timeStamp || 0;
      vx = 0;
      // NO setPointerCapture here. Capturing on the panel retargets pointerup to
      // the panel, and `click` is dispatched to the nearest common ancestor of
      // pointerdown and pointerup — so every button INSIDE the drawer (checkout,
      // qty +/-, remove, close) stops firing its click. Capture only once the
      // drag is genuinely claimed, by which point suppressing the click is
      // exactly what we want.
    }

    function claim(e) {
      dragging = true;
      pending = null;
      root.classList.add('is-dragging');
      if (anim) { anim.stop(); anim = null; }
      try { panel.setPointerCapture(e.pointerId); } catch (err) {}
    }

    function onMove(e) {
      var t = e.timeStamp || 0;
      var dt = t - lastT;
      if (dt > 0) {
        vx = ((e.clientX - lastX) / dt) * 1000;
        lastX = e.clientX;
        lastT = t;
      }

      if (!dragging) {
        if (!pending) return;
        var adx = Math.abs(e.clientX - pending.x);
        var ady = Math.abs(e.clientY - pending.y);
        // §10: detect both plausible gestures in parallel, then cancel the
        // loser. The drawer body scrolls vertically, so a vertical intent must
        // never be stolen by the sheet.
        if (ady > 10 && ady > adx) { pending = null; return; }
        if (adx > 8 && adx > ady) claim(e);
        else return;
      }

      var raw = baseX + (e.clientX - startX);
      // Past the open position there is nothing more to reveal, so resist
      // progressively instead of stopping dead (§9).
      if (raw * sign < 0) raw = rubber(raw, w);
      paint(raw);
    }

    function onUp(e) {
      try {
        if (panel.hasPointerCapture(e.pointerId)) panel.releasePointerCapture(e.pointerId);
      } catch (err) {}
      pending = null;
      if (!dragging) return;
      dragging = false;

      // §6: decide from where the gesture was GOING, not where it stopped.
      var landing = x + project(vx);
      var dismiss = landing * sign > w * 0.35;
      // §5 + "decide reverse vs commit from the velocity SIGN": a decisive
      // flick beats distance in both directions.
      if (Math.abs(vx) > 400) dismiss = vx * sign > 0;

      if (dismiss) tap(10);
      // §5 Velocity handoff: the spring starts at the finger's exact speed, so
      // there is no seam between dragging and animating.
      settle(dismiss ? w * sign : 0, vx, dismiss);
    }

    panel.addEventListener('pointerdown', onDown);
    panel.addEventListener('pointermove', onMove);
    panel.addEventListener('pointerup', onUp);
    panel.addEventListener('pointercancel', function (e) {
      pending = null;
      if (!dragging) return;
      dragging = false;
      settle(0, 0, false);
      onUp(e);
    });

    // Whenever the sheet is opened by a button, drop any inline transform left
    // over from a previous drag so the CSS transition owns the entrance again.
    // Guarded hard: this observer watches the very attribute release() writes.
    new MutationObserver(function () {
      if (dragging || anim) return;
      if (!root.classList.contains('is-open')) return;
      var dirty = panel.style.transform || (scrim && scrim.style.opacity) ||
        root.classList.contains('is-dragging');
      if (dirty) release();
    }).observe(root, { attributes: true, attributeFilter: ['class'] });
  }

  function initSheets() {
    var drawer = $('[data-cart-drawer]');
    if (drawer) {
      // Anchored inset-inline-start:0 = physical RIGHT under dir="rtl",
      // so it exits toward +X. Same path in as out (§7).
      draggableSheet(drawer, $('.cart-drawer__panel', drawer), $('.cart-drawer__scrim', drawer), 1);
    }
    var nav = $('[data-mobilenav]');
    if (nav) {
      // Anchored inset-inline-end:0 = physical LEFT under RTL → exits toward -X.
      draggableSheet(nav, $('.mobilenav__panel', nav), $('.mobilenav__scrim', nav), -1);
    }
  }

  /* §14 — "avoid full-viewport moving backgrounds". The hero and the story
     cover both autoplay a looping video behind the content; under reduced
     motion that is exactly the vestibular trigger the setting exists to stop.
     The poster frame stays, so the section still reads as designed. */
  function initReducedVideo() {
    if (!reduced) return;
    var bg = document.querySelectorAll(
      '.hero video, .abt-cover__bg, .abt-cta__bg, .media-strip video'
    );
    Array.prototype.forEach.call(bg, function (v) {
      try {
        v.autoplay = false;
        v.removeAttribute('autoplay');
        v.pause();
        v.currentTime = 0;
      } catch (e) {}
    });
  }

  /* §13 add-to-cart haptic REMOVED (2026-08-14).
     It was implemented as a MutationObserver over document.body with
     subtree+attributes, i.e. a callback on every class change anywhere on the
     page. That is a permanent tax on scrolling for one 12ms buzz, and the
     skill's own Utility rule says feedback has to earn its place. The dismissal
     haptic stays: it fires from a real gesture commit, no observer needed. */

  function boot() {
    initSheets();
    initReducedVideo();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', initReducedVideo);
})();
